import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/api/client";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function CompanySettings() {
    const [s, setS] = useState(null);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const load = async () => { const { data } = await api.get("/company/settings"); setS(data); };
    useEffect(() => { load(); }, []);

    const submit = async (e) => {
        e.preventDefault(); setErr(""); setMsg("");
        try {
            const payload = {
                name: s.name, address: s.address, city: s.city, state: s.state, gstin: s.gstin,
                brand_color: s.brand_color,
                financial_year_start_month: Number(s.financial_year_start_month) || 4,
                financial_year_end_month: Number(s.financial_year_end_month) || 3,
                smtp_host: s.smtp_host, smtp_port: Number(s.smtp_port) || 587,
                smtp_user: s.smtp_user, smtp_password: s.smtp_password || "",
                smtp_from: s.smtp_from, smtp_use_tls: !!s.smtp_use_tls,
            };
            await api.put("/company/settings", payload);
            setMsg("Settings saved.");
            load();
        } catch (err) { setErr(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    };

    if (!s) return <div className="text-stone-500 text-sm">Loading…</div>;

    return (
        <div data-testid="settings-page" className="space-y-6 max-w-3xl">
            <div>
                <div className="text-[11px] uppercase tracking-wider text-stone-500 mb-1">Admin</div>
                <h1 className="text-3xl font-heading font-semibold text-stone-900">Company settings</h1>
                <p className="text-sm text-stone-500 mt-1">Company profile, financial year, and SMTP for notifications & password reset emails.</p>
            </div>

            <form onSubmit={submit} className="space-y-6">
                {msg && <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2">{msg}</div>}
                {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}

                <Section title="Company profile">
                    <F label="Company name">
                        <input data-testid="cs-name" value={s.name || ""} onChange={(e) => setS({ ...s, name: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                    </F>
                    <F label="GSTIN">
                        <input data-testid="cs-gstin" value={s.gstin || ""} onChange={(e) => setS({ ...s, gstin: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3 font-mono" />
                    </F>
                    <F label="Address" full>
                        <input value={s.address || ""} onChange={(e) => setS({ ...s, address: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                    </F>
                    <F label="City">
                        <input value={s.city || ""} onChange={(e) => setS({ ...s, city: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                    </F>
                    <F label="State">
                        <input data-testid="cs-state" value={s.state || ""} onChange={(e) => setS({ ...s, state: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" />
                    </F>
                    <F label="Brand colour">
                        <input value={s.brand_color || "#F59E0B"} onChange={(e) => setS({ ...s, brand_color: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3 font-mono" />
                    </F>
                </Section>

                <Section title="Financial year">
                    <F label="Start month">
                        <select value={s.financial_year_start_month} onChange={(e) => setS({ ...s, financial_year_start_month: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                        </select>
                    </F>
                    <F label="End month">
                        <select value={s.financial_year_end_month} onChange={(e) => setS({ ...s, financial_year_end_month: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3">
                            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                        </select>
                    </F>
                </Section>

                <Section title="SMTP (email delivery)">
                    <F label="SMTP host"><input data-testid="cs-smtp-host" value={s.smtp_host || ""} onChange={(e) => setS({ ...s, smtp_host: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" placeholder="smtp.example.com" /></F>
                    <F label="SMTP port"><input type="number" value={s.smtp_port || 587} onChange={(e) => setS({ ...s, smtp_port: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" /></F>
                    <F label="SMTP username"><input value={s.smtp_user || ""} onChange={(e) => setS({ ...s, smtp_user: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" /></F>
                    <F label="SMTP password"><input type="password" value={s.smtp_password || ""} onChange={(e) => setS({ ...s, smtp_password: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" placeholder="(leave blank to keep current)" /></F>
                    <F label="From address" full><input value={s.smtp_from || ""} onChange={(e) => setS({ ...s, smtp_from: e.target.value })} className="w-full border border-stone-300 rounded-md text-sm py-2 px-3" placeholder="noreply@exprivia.com" /></F>
                    <F label="Use TLS" full>
                        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.smtp_use_tls} onChange={(e) => setS({ ...s, smtp_use_tls: e.target.checked })} /> Enable STARTTLS (recommended for port 587)</label>
                    </F>
                </Section>

                <div className="flex justify-end pt-2">
                    <button data-testid="save-settings" type="submit" className="bg-amber-500 hover:bg-amber-600 text-white rounded-md px-6 py-2.5 text-sm font-medium">Save settings</button>
                </div>
            </form>
        </div>
    );
}

function Section({ title, children }) {
    return (
        <div className="bg-white border border-stone-200 rounded-xl p-6">
            <div className="font-heading font-semibold text-stone-900 mb-4">{title}</div>
            <div className="grid md:grid-cols-2 gap-4">{children}</div>
        </div>
    );
}
function F({ label, children, full }) {
    return (
        <div className={full ? "md:col-span-2" : ""}>
            <label className="overline block mb-1">{label}</label>
            {children}
        </div>
    );
}
